import { Search } from "@mui/icons-material";
import { Box, Button, FormControl, IconButton, Paper, TextField, Typography } from "@mui/material";
import ShireTable from "components/ShireTable";
import { useEffect, useState } from "react";
import { PaginationModel } from "utils/PaginationType";
import { UpdateVoucherDto, Voucher } from "./VoucherType";
import { VoucherTableColumns } from "./VoucherModel";
import VoucherModal from "./VoucherModal";
import { getVouchers } from "services/VoucherService";
import useAuth from "hooks/useAuth";
import { publishMessage } from "components/ShireMessage";

export type SearchVoucherParams = {
  search: string;
}

export const VoucherManagement = () => {
  const [searchValue, setSearchValue] = useState('');
  const [searchParams, setSearchParams] = useState<SearchVoucherParams>({
    search: '',
  });
  const [paginationModel, setPaginationModel] = useState<PaginationModel>({
    page: 0,
    pageSize: 10,
    rowCount: 0,
  });
  const [vouchers, setVouchers] = useState<any[]>([]);
  const [totalVouchers, setTotalVouchers] = useState(0);
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [initialModalValues, setInitialModalValues] = useState<UpdateVoucherDto>({...UpdateVoucherDtoDefaultValues});

  const { user } = useAuth();
  const isExecutive = user?.role?.toUpperCase() === 'EXECUTIVE';

  useEffect(() => {
    fetchVouchers(paginationModel.page + 1, paginationModel.pageSize, searchParams);
  }, [paginationModel.page, paginationModel.pageSize, searchParams]);

  const fetchVouchers = async (page: number, pageSize: number, searchParams: SearchVoucherParams) => {
    const response = await getVouchers(page, pageSize, searchParams.search);
    setVouchers(response.records);
    setTotalVouchers(response.total);
  };

  const handleSearch = () => {
    setSearchParams(prev => ({ ...prev, search: searchValue }));
  };

  const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleEdit = (voucher: Voucher) => {

    if (isExecutive) {
      publishMessage({
        message: 'Bạn không có quyền sửa đổi voucher',
        variant: 'error',
      });
      return;
    }
    
    const voucherData: UpdateVoucherDto = {
      ...voucher,
      startDate: new Date(voucher.startDate),
      endDate: new Date(voucher.endDate),
    };
    
    // Use a single state update to avoid race conditions
    setInitialModalValues(voucherData);
    setIsEdit(true);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setIsEdit(false);
    setInitialModalValues({...UpdateVoucherDtoDefaultValues});
  };

  const handleDelete = (voucher: Voucher) => {
    if (isExecutive) {
      publishMessage({
        message: 'Bạn không có quyền xóa voucher',
        variant: 'error',
      });
      return;
    }
    console.log(voucher);
  };

  return <>
    <Typography variant="h4" sx={{ marginBottom: 3 }}>Quản lý voucher</Typography>
    <Paper elevation={3} sx={{ padding: 2, marginBottom: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
            <FormControl sx={{ flex: 1, position: 'relative' }}>
              <TextField
                id="search"
                name="search"
                placeholder="Search by name"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyUp={onKeyUp}
              />
            </FormControl>
            <IconButton
              color="primary"
              onClick={handleSearch}
              disabled={searchParams.search === ''}
              sx={{ position: 'absolute', right: 0 }}
            >
              <Search />
            </IconButton>
          </Box>

        {!isExecutive && <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
          Thêm mới voucher
        </Button>}
      </Box>
      <ShireTable
        columns={VoucherTableColumns(fetchVouchers, paginationModel, handleEdit, handleDelete)}
        rows={vouchers}
        paginationModel={{
          ...paginationModel,
          rowCount: totalVouchers,
        }}
        onPaginationModelChange={setPaginationModel}
        loading={false}
      />
    </Paper>
    <VoucherModal
      key={isEdit ? `edit-${initialModalValues.id}` : 'create'}
      open={open}
      handleClose={handleClose}
      isEdit={isEdit}
      initialValues={initialModalValues}
      reloadTableData={() => fetchVouchers(paginationModel.page + 1, paginationModel.pageSize, searchParams)}
    />
  </>;
};

const UpdateVoucherDtoDefaultValues: UpdateVoucherDto = {
  id: 0,
  name: '',
  code: '',
  description: '',
  imageUrl: '',
  externalId: '',
  voucherType: 'DISCOUNT',
  discountType: 'FIXED',
  discountValue: 0,
  exchangePoints: 0,
  maxCount: 0,
  startDate: new Date(),
  endDate: new Date(),
  status: 'ACTIVE',
  isPublic: true,
  expirePeriod: 0,
  eventType: undefined
};